import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import {
  ArrowLeft,
  Printer,
  Upload,
  X,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { renderRichContent } from '../../utils/richText';

const PrintPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement('div');
    el.id = 'print-portal-root';
    document.body.appendChild(el);
    setMountNode(el);
    return () => { document.body.removeChild(el); };
  }, []);
  if (!mountNode) return null;
  return createPortal(children, mountNode);
};

const getRefName = (ref: any): string => {
  if (ref == null) return '';
  if (typeof ref === 'object' && ref !== null && typeof ref.name === 'string') return ref.name;
  if (typeof ref === 'string') return ref;
  return String(ref);
};

const ViewBOQPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [boq, setBOQ] = useState<any>(null);
  const [activeView, setActiveView] = useState<number>(0);
  const [headerImage, setHeaderImage] = useState<string | null>(null);
  const [footerImage, setFooterImage] = useState<string | null>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const footerInputRef = useRef<HTMLInputElement>(null);

  const fetchBOQ = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiService.getBOQ(id);
      setBOQ(response.data);
    } catch (error: any) {
      console.error('Error fetching BOQ:', error);
      toast.error(error.response?.data?.error || 'Failed to load BOQ');
      navigate('/boqs');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) fetchBOQ();
    const savedHeader = localStorage.getItem('boq-header-image');
    const savedFooter = localStorage.getItem('boq-footer-image');
    if (savedHeader) setHeaderImage(savedHeader);
    if (savedFooter) setFooterImage(savedFooter);
  }, [id, fetchBOQ]);

  useEffect(() => {
    if (boq && boq.schedules?.length > 0 && (activeView < 0 || activeView >= boq.schedules.length)) {
      setActiveView(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boq?.schedules?.length, activeView]);

  const handleHeaderImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setHeaderImage(result);
        localStorage.setItem('boq-header-image', result);
        toast.success('Header image uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFooterImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setFooterImage(result);
        localStorage.setItem('boq-footer-image', result);
        toast.success('Footer image uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeHeaderImage = () => {
    setHeaderImage(null);
    localStorage.removeItem('boq-header-image');
    toast.success('Header image removed');
  };

  const removeFooterImage = () => {
    setFooterImage(null);
    localStorage.removeItem('boq-footer-image');
    toast.success('Footer image removed');
  };

  const handlePrint = () => {
    if (!boq) {
      toast.error('BOQ data not loaded');
      return;
    }
    window.print();
  };

  const renderDocumentInfo = () => (
    <div className="mb-6 text-xs font-sans">
      <div className="flex justify-between mb-4">
        <div>
          <p className="mb-1"><strong>Ref:</strong> {boq.boqNumber}</p>
          <p><strong>Date:</strong> {new Date(boq.boqDate).toLocaleDateString('en-GB')}</p>
        </div>
      </div>

      <div className="mb-4">
        <p className="font-bold mb-1">To</p>
        <p className="font-bold">{boq.customer?.name || 'N/A'}</p>
        {boq.customer?.designation && <p>{boq.customer.designation}</p>}
        {boq.customer?.company && <p className="font-medium">{boq.customer.company}</p>}
        {boq.customer?.address && (
          <p className="text-gray-600">
            {typeof boq.customer.address === 'string' ? boq.customer.address : JSON.stringify(boq.customer.address)}
          </p>
        )}
      </div>

      <div className="mb-4">
        <p><strong>Project Name:</strong> {boq.projectName || 'N/A'}</p>
        <p><strong>Project Address:</strong> {boq.projectAddress || 'N/A'}</p>
      </div>

      {(boq.inquiryOf?.name || boq.inquiryOf?.designation) && (
        <div className="mb-4">
          <p><strong>Inquiry of:</strong> {getRefName(boq.inquiryOf)} {boq.inquiryOf?.designation ? `- ${boq.inquiryOf.designation}` : ''}</p>
        </div>
      )}

      <div className="mb-4">
        <p><strong>Subject:</strong> Bill of Quantities for Air Conditioning system for above mentioned project.</p>
      </div>
    </div>
  );

  const renderClosingStatement = () => (
    <div className="mt-8 pt-4 border-t border-gray-300 quotation-footnote text-xs font-sans">
      <p className="mb-2">
        We hope that you will award the work order to us. We request you to call us for further any clarification.
      </p>
      <p className="mb-2">
        Thanking with the assurance of our best services at all times.
      </p>
      <div className="mt-4 avoid-break">
        <p className="font-semibold mb-8">Truly yours</p>
        <div className="mt-4">
          <p className="font-bold">Md. Abdul Alim</p>
          <p>Managing Director</p>
          <p className="font-semibold">THE AIRCONS LTD.</p>
        </div>
      </div>
    </div>
  );

  const renderScheduleDetails = (currentSchedule: any, scheduleIndex: number) => {
    const spanColumns = 3;

    return (
      <div className="w-full">
        {renderDocumentInfo()}

        <h2 className="font-bold mb-3 text-sm">
          {currentSchedule.scheduleName || currentSchedule.scheduleDetails?.name || `Schedule ${scheduleIndex + 1}`}
          {currentSchedule.scheduleDescription && `: ${currentSchedule.scheduleDescription}`}
        </h2>

        <table className="w-full table-fixed border-collapse border border-black mb-4 text-[11px]">
          <thead>
            <tr className="bg-gray-50 print:bg-white text-center">
              <th className="border border-black p-1 w-10">Sl.</th>
              <th className="border border-black p-1 text-left">Items</th>
              <th className="border border-black p-1 w-12">Unit</th>
              <th className="border border-black p-1 w-12">Qty</th>
            </tr>
          </thead>
          <tbody>
            {currentSchedule.sections?.map((section: any, sectionIndex: number) => (
              <React.Fragment key={sectionIndex}>
                <tr className="bg-gray-100 print:bg-gray-50 section-header-row avoid-break">
                  <td className="border border-black p-1 text-center font-bold">{sectionIndex + 1}.0</td>
                  <td colSpan={spanColumns} className="border border-black p-1 font-bold break-words">{section.sectionName || section.sectionDetails?.name || `Section ${sectionIndex + 1}`}</td>
                </tr>
                {section.sectionDescription && (
                  <tr className="avoid-break">
                    <td className="border border-black" />
                    <td colSpan={spanColumns} className="border border-black p-1 italic text-[10px] break-words">{section.sectionDescription}</td>
                  </tr>
                )}
                {section.products?.map((product: any, productIndex: number) => {
                  const prod = product.product;
                  const name = product.name || (typeof prod === 'object' && prod?.name) || '-';
                  const description = product.description ?? (typeof prod === 'object' ? prod?.description : null);
                  const brand = product.brand ?? (typeof prod === 'object' ? getRefName(prod?.brand) : null);
                  const model = product.model ?? (typeof prod === 'object' ? prod?.model : null);
                  const specifications = product.specifications ?? (typeof prod === 'object' ? prod?.specifications : null);
                  const typeVal = typeof prod === 'object' ? getRefName(prod?.type) : null;
                  const compressorTypeVal = typeof prod === 'object' ? getRefName(prod?.compressorType) : null;
                  const countryOfOriginVal = typeof prod === 'object' ? getRefName(prod?.countryOfOrigin) : null;
                  const countryOfAssemblyVal = typeof prod === 'object' ? getRefName(prod?.countryOfAssembly) : null;
                  const detailsVal = typeof prod === 'object' ? prod?.details : null;
                  const coolingCapacityVal = typeof prod === 'object' ? prod?.coolingCapacity : null;
                  const serialNoVal = typeof prod === 'object' ? prod?.serialNo : null;
                  const partNoVal = typeof prod === 'object' ? prod?.partNo : null;

                  return (
                    <tr key={productIndex} className="avoid-break">
                      <td className="border border-black p-1 text-center align-top">{sectionIndex + 1}.{productIndex + 1}</td>
                      <td className="border border-black p-1 align-top break-words">
                        <p className="font-medium">{name}</p>
                        <div className="text-[9px] mt-1 space-y-0.5">
                          {brand && <p><strong>Brand:</strong> {brand}</p>}
                          {model && <p><strong>Model:</strong> {model}</p>}
                          {typeVal && <p><strong>Type:</strong> {typeVal}</p>}
                          {compressorTypeVal && <p><strong>Compressor Type:</strong> {compressorTypeVal}</p>}
                          {countryOfOriginVal && <p><strong>Country of Origin:</strong> {countryOfOriginVal}</p>}
                          {countryOfAssemblyVal && <p><strong>Country of Assembly:</strong> {countryOfAssemblyVal}</p>}
                          {detailsVal && <p><strong>Details:</strong> {detailsVal}</p>}
                          {description && <p><strong>Description:</strong> {description}</p>}
                          {specifications && <p><strong>Specifications:</strong> {specifications}</p>}
                          {coolingCapacityVal && <p><strong>Cooling Capacity:</strong> {coolingCapacityVal}</p>}
                          {serialNoVal && <p><strong>Serial No:</strong> {serialNoVal}</p>}
                          {partNoVal && <p><strong>Part No:</strong> {partNoVal}</p>}
                        </div>
                      </td>
                      <td className="border border-black p-1 text-center align-top break-words">{product.unit || '-'}</td>
                      <td className="border border-black p-1 text-center align-top">{product.qty ?? 0}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {boq.notes && (
          <div className="mb-4">
            <h3 className="font-bold mb-2 text-xs">Notes:</h3>
            <div className="text-xs space-y-1">
              {renderRichContent(boq.notes)}
            </div>
          </div>
        )}

        {boq.termsAndConditions && (
          <div className="mb-4">
            <h3 className="font-bold mb-2 text-xs">Terms and Conditions:</h3>
            <div className="text-xs space-y-1">
              {renderRichContent(boq.termsAndConditions)}
            </div>
          </div>
        )}

        {renderClosingStatement()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!boq) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-600 mb-4">BOQ not found</p>
        <Button onClick={() => navigate('/boqs')}>Go to BOQs</Button>
      </div>
    );
  }

  const schedules = boq.schedules || [];
  const currentSchedule = schedules[activeView];

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <style>{`
        .quotation-footnote,
        .quotation-footnote * {
          font-size: 0.75rem !important;
        }
      `}</style>
      <style>{`
        @media print {
          #root { display: none !important; }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #print-portal-root {
            display: block !important;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: auto;
            z-index: 99999;
          }
          @page { size: A4; margin: 0; }
          p, h1, h2, h3, h4, h5, h6 { margin-top: 0; }
          .print-layout-table { width: 100%; border-collapse: collapse; position: relative; z-index: 50; }
          .print-header-cell {
            height: 1.5in;
            vertical-align: top;
            padding: 0;
          }
          .print-header-cell img {
            width: 100%;
            height: 1.5in;
            object-fit: contain;
            object-position: top;
            display: block;
          }
          .print-footer-cell {
            height: 0.9in;
            vertical-align: bottom;
            padding: 0;
          }
          .print-footer-fixed {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 1.8in;
            z-index: -10;
            display: flex !important;
            align-items: flex-end;
            justify-content: center;
          }
          .print-footer-fixed img {
            width: 100%;
            height: 1.8in;
            object-fit: contain;
            object-position: bottom;
            display: block;
          }
          .print-content-cell {
            padding: 0 0.5in;
            vertical-align: top;
          }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          .avoid-break { page-break-inside: avoid; break-inside: avoid; }
          .quotation-footnote, .quotation-footnote * { font-size: 0.75rem !important; }
        }
      `}</style>

      {/* Screen header */}
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="sticky top-0 z-50 bg-white border-b px-6 py-4 shadow-sm print:hidden">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/boqs')}>Back</Button>
            <div>
              <h1 className="text-lg font-bold">BOQ {boq.boqNumber}</h1>
              <p className="text-xs text-gray-500">{boq.customer?.name || 'N/A'} - {boq.projectName || 'N/A'}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/boqs/edit/${boq._id}`)}>Edit</Button>
            <Button variant="outline" size="sm" icon={<Printer className="w-4 h-4" />} onClick={handlePrint}>Print</Button>
          </div>
        </div>

        <div className="flex space-x-4 text-sm mb-4 border-t pt-4">
          <div className="flex items-center space-x-2">
            <input ref={headerInputRef} type="file" accept="image/*" onChange={handleHeaderImageUpload} className="hidden" />
            <Button variant="ghost" size="sm" icon={<Upload className="w-3 h-3" />} onClick={() => headerInputRef.current?.click()}>
              {headerImage ? 'Change Header' : 'Upload Header'}
            </Button>
            {headerImage && <Button variant="ghost" size="sm" icon={<X className="w-3 h-3" />} onClick={removeHeaderImage}>Remove</Button>}
          </div>
          <div className="flex items-center space-x-2">
            <input ref={footerInputRef} type="file" accept="image/*" onChange={handleFooterImageUpload} className="hidden" />
            <Button variant="ghost" size="sm" icon={<Upload className="w-3 h-3" />} onClick={() => footerInputRef.current?.click()}>
              {footerImage ? 'Change Footer' : 'Upload Footer'}
            </Button>
            {footerImage && <Button variant="ghost" size="sm" icon={<X className="w-3 h-3" />} onClick={removeFooterImage}>Remove</Button>}
          </div>
        </div>

        <div className="flex space-x-2 overflow-x-auto pb-2">
          {schedules.map((s: any, i: number) => (
            <button
              key={i}
              onClick={() => setActiveView(i)}
              className={`px-3 py-1 text-sm rounded-md whitespace-nowrap ${activeView === i ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}
            >
              {s.scheduleName || s.scheduleDetails?.name || `Schedule ${i + 1}`}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Preview container */}
      <div className="w-[210mm] mx-auto bg-white p-[0.5in] min-h-screen shadow-lg my-8 overflow-hidden">
        <div className="border-b-2 border-dashed border-gray-300 mb-4 pb-2 text-center text-gray-400 text-sm">(Preview Mode)</div>
        {currentSchedule ? renderScheduleDetails(currentSchedule, activeView) : (
          <div className="text-center text-gray-500 py-8">No schedule selected.</div>
        )}
      </div>

      {/* Print portal */}
      <PrintPortal>
        <div className="hidden print:block font-sans">
          <table className="print-layout-table">
            <thead>
              <tr>
                <td className="print-header-cell">
                  {headerImage && <img src={headerImage} alt="Header" />}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="print-content-cell">
                  {currentSchedule ? renderScheduleDetails(currentSchedule, activeView) : null}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td className="print-footer-cell">
                  <div style={{ height: '0.9in' }} />
                </td>
              </tr>
            </tfoot>
          </table>
          <div className="print-footer-fixed">
            {footerImage && <img src={footerImage} alt="Footer" />}
          </div>
        </div>
      </PrintPortal>
    </div>
  );
};

export default ViewBOQPage;
